import { escapeHtml } from "./pdfUtils";

const ACCENT = "#5ef0d8";
const LAVENDER = "#7fb4ff";

function trimLabelPunctuation(value) {
  return String(value || "").replace(/[:：]\s*$/, "");
}

function interpolate(template, vars = {}) {
  return String(template || "").replace(/%\{(\w+)\}/g, (_, key) => (
    Object.prototype.hasOwnProperty.call(vars, key) ? String(vars[key]) : `%{${key}}`
  ));
}

export function buildChineseZodiacPdfHtml({
  exportedAt,
  birthInfo,
  pillars,
  synthesisParagraphs,
  elementBalance,
  compatibleMatches,
  daily,
  labels = {},
}) {
  const l = labels;
  return `
    <link rel="preconnect" href="https://fonts.googleapis.com">
    <link href="https://fonts.googleapis.com/css2?family=Playfair+Display:ital,wght@0,400;0,500;0,700;1,400;1,500&family=DM+Sans:wght@300;400;500&family=DM+Mono:wght@300;400&display=swap" rel="stylesheet">
    <style>
      @page { size: A4; margin: 12mm; }
      * { box-sizing: border-box; }
      body {
        margin: 0;
        font-family: "DM Sans", "Inter", "Segoe UI", Arial, sans-serif;
        font-weight: 300;
        color: #e8ebff;
        background: #050816;
      }
      .sheet {
        position: relative;
        overflow: hidden;
        border-radius: 18px;
        background:
          radial-gradient(ellipse 60% 40% at 20% 100%, rgba(127,180,255,0.10) 0%, transparent 60%),
          radial-gradient(circle at top left, #172148, #080b1f 56%, #050816);
        border: 1px solid rgba(94,240,216,0.20);
      }
      .sheet::before {
        content: "";
        position: absolute;
        inset: 0;
        background-image:
          radial-gradient(1px 1px at 10% 15%, rgba(255,255,255,0.45) 0%, transparent 100%),
          radial-gradient(1px 1px at 25% 60%, rgba(255,255,255,0.30) 0%, transparent 100%),
          radial-gradient(1.2px 1.2px at 40% 30%, rgba(255,255,255,0.40) 0%, transparent 100%),
          radial-gradient(1px 1px at 55% 80%, rgba(255,255,255,0.25) 0%, transparent 100%),
          radial-gradient(1px 1px at 70% 20%, rgba(255,255,255,0.40) 0%, transparent 100%),
          radial-gradient(1.2px 1.2px at 80% 55%, rgba(255,255,255,0.30) 0%, transparent 100%);
        pointer-events: none;
        z-index: 0;
      }
      .sheet > * { position: relative; z-index: 1; }
      .header {
        display: flex;
        align-items: flex-end;
        justify-content: space-between;
        padding: 26px 28px 18px;
        border-bottom: 1px solid rgba(94,240,216,0.20);
      }
      .brand {
        font-family: "Playfair Display", "Georgia", serif;
        font-size: 22px;
        letter-spacing: 0.12em;
        color: ${ACCENT};
        text-transform: uppercase;
      }
      .brand span {
        display: block;
        font-family: "DM Sans", sans-serif;
        font-size: 10px;
        font-weight: 400;
        letter-spacing: 0.25em;
        color: #8a8ea8;
        text-transform: uppercase;
        margin-top: 4px;
      }
      .header-date {
        text-align: right;
        font-family: "DM Mono", monospace;
        font-size: 10.5px;
        color: #8a8ea8;
        letter-spacing: 0.08em;
        line-height: 1.7;
      }
      .header-date strong {
        display: block;
        color: ${LAVENDER};
        font-size: 12px;
        letter-spacing: 0.15em;
        margin-bottom: 2px;
      }
      .content {
        padding: 22px 24px 24px;
        display: grid;
        grid-template-columns: 1fr 1fr;
        gap: 14px;
      }
      .full { grid-column: 1 / -1; }
      .section-divider {
        grid-column: 1 / -1;
        font-size: 10px;
        letter-spacing: 0.3em;
        text-transform: uppercase;
        color: ${ACCENT};
        margin: 6px 0 -2px;
        padding-bottom: 6px;
        border-bottom: 1px solid rgba(94,240,216,0.15);
        font-family: "DM Mono", monospace;
      }
      .pillars-grid { grid-column: 1 / -1; display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 12px; }
      .card {
        position: relative;
        overflow: hidden;
        background: rgba(19,17,42,0.85);
        border: 1px solid rgba(94,240,216,0.20);
        padding: 14px 16px;
        border-radius: 10px;
      }
      .card::before {
        content: "";
        position: absolute;
        top: 0;
        left: 0;
        width: 2px;
        height: 100%;
        background: linear-gradient(to bottom, ${ACCENT}, transparent);
        opacity: 0.6;
      }
      .label {
        font-size: 9px;
        letter-spacing: 0.3em;
        text-transform: uppercase;
        color: ${ACCENT};
        margin-bottom: 8px;
        font-weight: 500;
      }
      .value {
        font-size: 18px;
        font-family: "Playfair Display", "Georgia", serif;
        margin-bottom: 4px;
        color: #ffffff;
      }
      .sub { font-size: 11.5px; color: #c3cced; line-height: 1.55; }
      .daily-meta { font-size: 10.5px; color: #8fa3d0; margin-top: 10px; }
      .section { margin-top: 0; }
      .section-title {
        font-size: 10px;
        letter-spacing: 0.3em;
        margin-bottom: 8px;
        color: ${ACCENT};
        text-transform: uppercase;
        font-family: "DM Mono", monospace;
      }
      .synthesis {
        padding: 14px;
        border: 1px solid rgba(94,240,216,0.35);
        border-radius: 10px;
        background: linear-gradient(180deg, rgba(94,240,216,0.10), rgba(94,240,216,0.04));
        color: #d8f5ee;
        font-size: 12px;
        line-height: 1.65;
      }
      .synthesis p { margin: 0 0 8px; }
      .synthesis p:last-child { margin-bottom: 0; }
      .balance { display: grid; grid-template-columns: repeat(5, minmax(0, 1fr)); gap: 8px; margin-top: 8px; }
      .balance-item { border: 1px solid rgba(94,240,216,0.18); border-radius: 8px; padding: 10px; background: rgba(255,255,255,0.02); text-align: center; }
      .compat-list { border: 1px solid rgba(94,240,216,0.20); border-radius: 10px; overflow: hidden; background: rgba(19,17,42,0.85); }
      .compat-row { display: grid; grid-template-columns: 1fr auto; gap: 8px; padding: 9px 12px; border-top: 1px solid rgba(94,240,216,0.10); font-size: 11.5px; color: #eef2ff; }
      .compat-row:first-child { border-top: none; }
      .compat-row span:last-child { color: ${LAVENDER}; font-family: "DM Mono", monospace; }
      .footer {
        grid-column: 1 / -1;
        margin-top: 2px;
        padding-top: 14px;
        border-top: 1px dashed rgba(94,240,216,0.20);
        font-family: "DM Mono", monospace;
        font-size: 10px;
        color: #8a8ea8;
        letter-spacing: 0.12em;
        display: flex;
        justify-content: space-between;
      }
      @media print {
        html, body {
          background: #050816 !important;
          -webkit-print-color-adjust: exact;
          print-color-adjust: exact;
        }
        .sheet {
          overflow: visible;
          border-radius: 0;
          border: none;
          background: #050816 !important;
        }
        .sheet::before { display: none !important; }
        .card, .compat-list, .balance-item {
          background: #13112a !important;
        }
        .card::before { display: none !important; }
      }
    </style>
    <main class="sheet">
      <header class="header">
        <div class="brand">
          Ecliptica
          <span>${escapeHtml(l.eyebrow)}</span>
        </div>
        <div class="header-date">
          <strong>${escapeHtml(l.exported)}</strong>
          ${escapeHtml(exportedAt)}<br/>
          ${escapeHtml(l.birthDetails)}: ${escapeHtml(birthInfo)}
        </div>
      </header>
      <section class="content">
        ${daily ? `<div class="section-divider">${escapeHtml(l.dailySection)}</div>` : ""}
        ${daily ? `
          <div class="card full">
            <div class="label">${escapeHtml(l.dailyTheme)}</div>
            <div class="sub">${escapeHtml(daily.summary || "")}</div>
            <div class="daily-meta">${escapeHtml(trimLabelPunctuation(l.luckyColor))}: ${escapeHtml(daily.luckyColor || "—")} · ${escapeHtml(trimLabelPunctuation(l.avoidColor))}: ${escapeHtml(daily.avoidColor || "—")}</div>
          </div>
          <div class="card">
            <div class="label">${escapeHtml(l.dailyRelationships)}</div>
            <div class="sub">${escapeHtml(daily.relationships || "")}</div>
          </div>
          <div class="card">
            <div class="label">${escapeHtml(l.dailyCareer)}</div>
            <div class="sub">${escapeHtml(daily.career || "")}</div>
          </div>
          <div class="card full">
            <div class="label">${escapeHtml(l.dailyWellness)}</div>
            <div class="sub">${escapeHtml(daily.wellness || "")}</div>
          </div>
        ` : ""}
        <div class="section-divider">${escapeHtml(l.title)}</div>
        <div class="pillars-grid">
          ${pillars.map((pillar) => `
            <div class="card">
              <div class="label">${escapeHtml(pillar.label)} ${escapeHtml(l.pillar)}</div>
              <div class="value">${escapeHtml(pillar.animalName)} · ${escapeHtml(pillar.element)}</div>
              <div class="sub">${escapeHtml(pillar.period)} · ${escapeHtml(pillar.role)}</div>
            </div>
          `).join("")}
        </div>
        <div class="section full">
          <div class="section-title">${escapeHtml(l.destinySynthesis)}</div>
          <div class="synthesis">
            ${synthesisParagraphs.map((text) => `<p>${escapeHtml(text)}</p>`).join("")}
          </div>
        </div>
        <div class="section full">
          <div class="section-title">${escapeHtml(l.elementalBalance)}</div>
          <div class="balance">
            ${elementBalance.map((item) => `<div class="balance-item"><div class="label">${escapeHtml(item.el)}</div><div class="value" style="font-size:16px;margin:0;color:${ACCENT};">${escapeHtml(interpolate(l.elementCountOfFour, { count: item.count }))}</div></div>`).join("")}
          </div>
        </div>
        <div class="section full">
          <div class="section-title">${escapeHtml(l.bestMatches)}</div>
          <div class="compat-list">
            ${compatibleMatches.map((match) => `<div class="compat-row"><span>${escapeHtml(match.name)}</span><span>${escapeHtml(match.tier)}</span></div>`).join("")}
          </div>
        </div>
        <div class="footer">
          <span>${escapeHtml(l.generatedBy)}</span>
          <span>${escapeHtml(exportedAt)}</span>
        </div>
      </section>
    </main>
  `;
}
